import { useEffect } from 'react';
import { Outlet, Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import {
  LogOut,
  LayoutDashboard,
  FolderOpen,
  Briefcase,
  Users,
  MessageSquare,
  Mail,
  Settings,
  FileText,
  BarChart,
  User,
  ChevronsUpDown,
  Search
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GlassmorphismSidebar } from '@/components/admin/GlassmorphismSidebar';

export default function AdminLayout() {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();

  // Se não estiver logado e não estiver carregando, redirecionar para login
  if (!loading && !user) {
    return <Navigate to="/admin/login" replace />;
  }

  // Se estiver carregando, mostrar loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const navigation = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: LayoutDashboard,
      active: location.pathname === '/admin'
    },
    {
      name: 'Projetos',
      href: '/admin/projetos',
      icon: FolderOpen,
      active: location.pathname.startsWith('/admin/projetos')
    },
    {
      name: 'Serviços',
      href: '/admin/servicos',
      icon: Briefcase,
      active: location.pathname.startsWith('/admin/servicos')
    },
    {
      name: 'Clientes',
      href: '/admin/clientes',
      icon: Users,
      active: location.pathname.startsWith('/admin/clientes')
    },
    {
      name: 'Depoimentos',
      href: '/admin/depoimentos',
      icon: MessageSquare,
      active: location.pathname.startsWith('/admin/depoimentos')
    },
    {
      name: 'Conteúdo',
      href: '/admin/conteudo',
      icon: FileText,
      active: location.pathname.startsWith('/admin/conteudo')
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: BarChart,
      active: location.pathname.startsWith('/admin/analytics')
    },
    {
      name: 'Contato',
      href: '/admin/contato',
      icon: Mail,
      active: location.pathname.startsWith('/admin/contato')
    },
    {
      name: 'Configurações',
      href: '/admin/configuracoes',
      icon: Settings,
      active: location.pathname.startsWith('/admin/configuracoes')
    },
  ];

  // Helper para gerar breadcrumbs
  const getBreadcrumbs = () => {
    const path = location.pathname;

    if (path === '/admin') return [{ name: 'Dashboard', href: '/admin', current: true }];

    const parts = path.split('/').filter(Boolean);
    // Remove 'admin' from parts for processing if desired, but let's keep it simple

    const items = [
      { name: 'Dashboard', href: '/admin', current: false }
    ];

    if (parts[1]) {
      const name = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      const isLast = parts.length === 2;
      items.push({
        name: name,
        href: `/admin/${parts[1]}`,
        current: isLast
      });

      if (parts[2]) {
        if (parts[2] === 'novo') {
          items.push({ name: 'Novo', href: path, current: true });
        } else if (parts[3] === 'editar') {
          items.push({ name: 'Editar', href: path, current: true });
        } else {
          // Fallback for details etc
          items.push({ name: 'Detalhes', href: path, current: true });
        }
      }
    }

    return items;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="dark h-full">
      <SidebarProvider className="bg-background text-foreground">
        <GlassmorphismSidebar />
        <SidebarInset>
          <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 border-b bg-background/50 backdrop-blur-sm sticky top-0 z-10 px-4">
            <div className="flex items-center gap-2 px-4">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-2 h-4" />
              <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbs.map((item, index) => (
                    <div key={item.href} className="flex items-center">
                      {index > 0 && <BreadcrumbSeparator className="mx-2" />}
                      <BreadcrumbItem className="hidden md:block">
                        {item.current ? (
                          <BreadcrumbPage>{item.name}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild>
                            <Link to={item.href}>{item.name}</Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                    </div>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </header>
          <main className="flex flex-1 flex-col gap-4 p-4 pt-0">
            <div className="mx-auto w-full max-w-7xl animate-in fade-in slide-in-from-bottom-4 duration-500 py-6">
              <Outlet />
            </div>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
